import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Scale, FileText, Trash2, Cookie, AlertTriangle, Mail, ExternalLink } from 'lucide-react';

export default function Footer({ variant = 'full' }) {
  const currentYear = new Date().getFullYear();

  // Compact version for app pages
  if (variant === 'compact') {
    return (
      <footer className="py-6 px-4" style={{ background: 'rgba(10, 10, 27, 0.8)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm" style={{ color: 'rgba(229,231,235,0.4)' }}>
            © {currentYear} Privluxe. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm" style={{ color: 'rgba(229,231,235,0.5)' }}>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
          </div>
        </div>
      </footer>
    );
  }

  // Full footer for landing page
  return (
    <footer style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #07070F 100%)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}>
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Privluxe</span>
            </div>
            <p className="text-sm mb-4" style={{ color: 'rgba(229,231,235,0.6)' }}>
              Your conversations are private. Designed for discretion, built for security.
            </p>
            <p className="text-sm" style={{ color: 'rgba(229,231,235,0.4)' }}>
              No app. No traces. Full control.
            </p>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Scale className="h-4 w-4" style={{ color: '#A78BFA' }} />
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/terms" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <FileText className="h-3.5 w-3.5" />
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Lock className="h-3.5 w-3.5" />
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/acceptable-use" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Shield className="h-3.5 w-3.5" />
                  Acceptable Use Policy
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Disclaimer
                </Link>
              </li>
            </ul>
          </div>

          {/* Privacy & Data */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Lock className="h-4 w-4" style={{ color: '#22D3EE' }} />
              Privacy & Data
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/cookies" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Cookie className="h-3.5 w-3.5" />
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link to="/data-deletion" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Trash2 className="h-3.5 w-3.5" />
                  Data Deletion
                </Link>
              </li>
              <li>
                <Link to="/law-enforcement" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Scale className="h-3.5 w-3.5" />
                  Law Enforcement Guidelines
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="h-4 w-4" style={{ color: '#22C55E' }} />
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:support@privluxe.com" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Mail className="h-3.5 w-3.5" />
                  support@privluxe.com
                </a>
              </li>
              <li>
                <a href="mailto:privacy@privluxe.com" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Lock className="h-3.5 w-3.5" />
                  privacy@privluxe.com
                </a>
              </li>
              <li>
                <a href="mailto:legal@privluxe.com" className="text-sm flex items-center gap-2 transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.6)' }}>
                  <Scale className="h-3.5 w-3.5" />
                  legal@privluxe.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Message */}
        <div className="p-6 rounded-2xl mb-12 text-center" style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.1)' }}>
          <p style={{ color: 'rgba(229,231,235,0.7)' }}>
            <span style={{ color: '#22D3EE' }}>Messages are protected with advanced security protocols.</span><br />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.5)' }}>We do not access your personal conversations. Your privacy is your power.</span>
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-sm" style={{ color: 'rgba(229,231,235,0.4)' }}>
            © {currentYear} Privluxe. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-xs transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.5)' }}>Terms</Link>
            <Link to="/privacy" className="text-xs transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.5)' }}>Privacy</Link>
            <Link to="/cookies" className="text-xs transition-colors hover:text-white" style={{ color: 'rgba(229,231,235,0.5)' }}>Cookies</Link>
            <a 
              href="https://www.privluxe.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs flex items-center gap-1 transition-colors hover:text-white" 
              style={{ color: 'rgba(229,231,235,0.5)' }}
            >
              www.privluxe.com
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
